import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { paginatedResponse } from "../utils";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";


export function useGetAllClauses(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["clauses", Object.fromEntries(params.entries())],
        queryFn: () => {
            return clausesApi.getAllClauses(axiosPriv, params);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}


export function useClauseById(id, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["clauses", id],
        queryFn: () => {
            return clausesApi.getClauseDetails(axiosPriv, id);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCreateClauseMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["clauses"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ data }) => clausesApi.createClause(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateClauseMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["clauses"] });
            await queryClient.invalidateQueries({ queryKey: ["clauses", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => clausesApi.updateClause(axiosPriv, id, data),
        onError: onServerError,
    });
}


export function useGetPersonClauses(personId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["clauses", "person", personId],
        queryFn: () => {
            return clausesApi.getPersonClauses(axiosPriv, personId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useGetPersonClausesHistory(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["person-clauses", Object.fromEntries(params.entries())],
        queryFn: () => {
            return clausesApi.getPersonClausesHistory(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useSetPersonClauseMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["clauses", "person", variables.personId],
            });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ clauseId, personId, clauseComment, value, sendEmail }) => {
            return clausesApi.setPersonClause(axiosPriv, {
                clauseId,
                personId,
                clauseComment,
                value,
                sendEmail,
            });
        },
    });
}

export function useSubscribeClausesMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["clauses", "person"],
            });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ personLeadId, clauses }) => {
            return clausesApi.subscribeClauses(axiosPriv, {
                personLeadId,
                clauses
            });
        },
    });
}

export function useUnsubscribeClausesMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["clauses", "person"],
            });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ personLeadId }) => {
            return clausesApi.unsubscribeClauses(axiosPriv, {
                personLeadId,
            });
        },
    });
}

export const ClauseSchema = z.object({
    clause_id: z.string(),
    clause_name_eng: z.string(),
    clause_text: z.string(),
});

export const PersonClauseSchema = z.object({
    id: z.string(),
    clause_name_eng: z.string(),
    persclau_decision: z.boolean(),
    created: z.string(),
});

export const clausesApiSchema = {
    getAllClausesResponse: paginatedResponse(ClauseSchema),
    getPersonClausesResponse: paginatedResponse(PersonClauseSchema),
};
export const clausesApi = {
    getClauseDetails: (axios, id ) => {
        return axios
            .get(apiUrls.customers.clauses.details(id))
            .then((res) => res.data);
    },

    createClause: (axios, body) => {
        return axios.post(
            apiUrls.customers.clauses.all,
            body,
        );
    },

    updateClause: (axios, id, body) => {
        return axios.patch(
            apiUrls.customers.clauses.details(id),
            body,
        );
    },

    getAllClauses: (axios, params) => {
        return axios
            .get(apiUrls.customers.clauses.all, {params})
            .then((res) => res.data);
    },

    getPersonClauses: (axios, personId) => {
        return axios
            .get(apiUrls.customers.persons.clauses(personId))
            .then((res) => res.data);
    },

    getPersonClausesHistory: (axios, params) => {
        return axios
            .get(apiUrls.customers.personClause, {params})
            .then((res) => res.data);
    },

    setPersonClause: (axios, { clauseId, personId, clauseComment, value, sendEmail }) => {
        return axios.post(apiUrls.customers.personClause, {
                persclau_clause: clauseId,
                persclau_person: personId,
                persclau_comment: clauseComment,
                persclau_decision: value,
                send_email: sendEmail
            })
            .then((res) => res.data);
    },

    subscribeClauses: (axios, { personLeadId, clauses }) => {
        return axios.post(apiUrls.customers.clauses.subscribe, {
                person_lead_id: personLeadId,
                clauses: clauses
            })
            .then((res) => res.data);
    },

    unsubscribeClauses: (axios, { personLeadId }) => {
        return axios.post(apiUrls.customers.clauses.unsubscribe, {
                person_lead_id: personLeadId,
            })
            .then((res) => res.data);
    },
};
